/**
 * /transport — Public transporter directory (Phase 4)
 * ----------------------------------------------------
 * Browsable, filterable list of every verified, payout-ready Armstrong
 * Transport hauler. Anonymous + authenticated buyers both see this.
 *
 *  - State filter (chip row from /api/transport/states)
 *  - Sort: trips · newest · name
 *  - "Request a transport" CTA at the top
 *  - Cards link to /transport/profile/:slug
 */
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { TruckIcon as Truck, ShieldCheckIcon as ShieldCheck, MapPinIcon as MapPin, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";

const SORT_OPTIONS = [
  { v: "trips", label: "Most experienced" },
  { v: "newest", label: "Newest first" },
  { v: "name", label: "Name A→Z" },
];

export default function TransportDirectory() {
  const [transporters, setTransporters] = useState([]);
  const [states, setStates] = useState([]);
  const [activeState, setActiveState] = useState("");
  const [sort, setSort] = useState("trips");
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ sort, limit: "50" });
      if (activeState) params.set("state", activeState);
      const res = await api.get(`/transport/directory?${params.toString()}`);
      setTransporters(res.data.transporters || []);
      setTotal(res.data.total || 0);
    } finally {
      setLoading(false);
    }
  }, [activeState, sort]);

  useEffect(() => {
    api.get("/transport/states").then((r) => setStates(r.data.states || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="transport-directory">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-2">
          <div>
            <h1 className="font-spectral text-4xl tracking-tight flex items-center gap-3">
              <Truck size={32} weight="duotone" className="text-ink" />
              Armstrong Transport
            </h1>
            <p className="text-[13px] text-[#6B6B6B] mt-1 max-w-2xl leading-relaxed">
              USDA-licensed, fully insured, Armstrong-verified haulers.
              Browse the roster, view their service area + trip history, or
              submit a request and let multiple haulers bid for your route.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 sm:flex-shrink-0">
            <Link to="/transport/request">
              <Button className="rounded-full bg-crimson hover:bg-ink text-white text-[12px] h-9 px-4" data-testid="cta-request">
                Request a transport
              </Button>
            </Link>
            <Link to="/transport/apply">
              <Button variant="outline" className="rounded-full text-[12px] h-9 px-4" data-testid="cta-apply">
                Become a transporter
              </Button>
            </Link>
            <Link to="/transport/getting-started">
              <Button variant="ghost" className="rounded-full text-[12px] h-9 px-3 underline-offset-2" data-testid="cta-getting-started">
                How it works →
              </Button>
            </Link>
            <Link to="/transport/international">
              <Button variant="ghost" className="rounded-full text-[12px] h-9 px-3 underline-offset-2 text-[#8A6A00]" data-testid="cta-intl">
                Importing from abroad? →
              </Button>
            </Link>
          </div>
        </div>

        {/* Filter row */}
        <div className="mt-5 mb-4 flex flex-wrap items-center gap-2" data-testid="filter-row">
          <span className="text-[11px] uppercase tracking-wider font-bold text-[#6B6B6B] mr-1">
            Filter by state
          </span>
          <button
            type="button"
            onClick={() => setActiveState("")}
            className={`text-[11px] h-7 px-2.5 rounded-full border ${
              activeState === ""
                ? "bg-ink border-ink text-white"
                : "bg-white border-[#E5E5E5] text-[#444] hover:bg-[#FAFAFA]"
            }`}
            data-testid="state-filter-all"
          >
            All ({total})
          </button>
          {states.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setActiveState(s === activeState ? "" : s)}
              className={`text-[11px] h-7 px-2.5 rounded-full border ${
                s === activeState
                  ? "bg-ink border-ink text-white"
                  : "bg-white border-[#E5E5E5] text-[#444] hover:bg-[#FAFAFA]"
              }`}
              data-testid={`state-filter-${s}`}
            >
              {s}
            </button>
          ))}
          <div className="flex-1" />
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="text-[12px] h-8 px-2 border border-[#E5E5E5] rounded-md bg-white"
            data-testid="sort-select"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.v} value={o.v}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Cards */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} className="h-40" />)}
          </div>
        ) : transporters.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-[#E5E5E5] rounded-xl" data-testid="no-transporters">
            <Truck size={32} weight="duotone" className="text-[#9A9A9A] mx-auto mb-2" />
            <p className="text-[14px] text-[#6B6B6B]">
              {activeState
                ? `No verified transporters cover ${activeState} yet.`
                : "The roster is empty — be the first to apply."}
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {transporters.map((t) => <TransporterCard key={t.id} t={t} />)}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function TransporterCard({ t }) {
  return (
    <Link
      to={`/transport/profile/${t.slug}`}
      className="border border-[#E5E5E5] bg-white rounded-2xl overflow-hidden hover:border-ink hover:shadow-md transition-all group flex flex-col"
      data-testid={`carrier-card-${t.slug}`}
    >
      {t.rig_photo_url ? (
        <div
          className="h-32 bg-cover bg-center"
          style={{ backgroundImage: `url(${t.rig_photo_url})` }}
        />
      ) : (
        <div className="h-32 bg-gradient-to-br from-[#F4EFE6] to-[#E8E2D2] flex items-center justify-center">
          <Truck size={36} weight="duotone" className="text-[#9A8B6E]" />
        </div>
      )}
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-baseline justify-between gap-2 mb-1">
          <p className="font-spectral text-xl tracking-tight truncate">{t.display_name}</p>
          {t.verified_badge_at && (
            <ShieldCheck size={16} weight="duotone" className="text-emerald-600 flex-shrink-0" />
          )}
        </div>
        <p className="text-[12px] text-[#6B6B6B] flex items-center gap-1 mb-1">
          <MapPin size={11} weight="duotone" />
          {(t.service_area_states || []).slice(0, 6).join(" · ")}
          {(t.service_area_states || []).length > 6 && "…"}
        </p>
        <p className="text-[12px] text-[#444] mt-auto pt-2 border-t border-[#EAEAEA] flex items-center justify-between">
          <span>
            <strong>{t.trips_completed ?? 0}</strong> trip
            {(t.trips_completed ?? 0) === 1 ? "" : "s"} completed
          </span>
          <span className="inline-flex items-center gap-1 text-ink group-hover:gap-1.5 transition-all">
            View <ArrowRight size={11} />
          </span>
        </p>
      </div>
    </Link>
  );
}
